/**
 * @fileoverview Own the matter workspace connecting shared case context to focused domain tabs.
 *
 * Route/tab state supports navigation; backend matter identity and permissions
 * remain authoritative. Child domains own their business actions, and shared
 * state must reconcile with those actions when tabs or matters change.
 *
 * `kc-internal-backend/functions/case_details.py` owns the case response;
 * `kc-internal-backend/functions/matters.py` owns matter operations;
 * `kc-internal-backend/functions/simul_archive_write.py` owns document versions;
 * `kc-internal-backend/functions/activity.py` owns contact/timeline integration.
 * Cross-tab refreshes must preserve these ownership boundaries and prevent old
 * matter responses from becoming current context.
 */

import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import ToastContext from '../../ToastContext';
import Billing from './Billing';
import Invoices from './Invoices';
import Clients from './Clients';
import CaseInfo from './CaseInfo';
import Activity from './Activity/Activity';
import api from '../../api';
import Attributes from './Attributes';
import QueuedTasks from './QueuedTasks';
import TrelloTaskForm from './TrelloTaskForm';
import MatterDocumentsTab from './MatterDocumentsTab';
import {
  setMatter } from
'../../store/CaseDetails/action';
import XeroFinancials from './XeroFinancials';
import DemandSentAttributeForm from './DemandSentAttributeForm';
import PaymentInstructionsHistory from './PaymentInstructionsHistory';
import ClientUpdateWorkModal from './ClientUpdateWorkModal';
import NotFoundPage from '../NotFoundPage';
import ConflictCheckPanel from '../ConflictChecks/ConflictCheckPanel';
import useConflictChecksEnabled from '../ConflictChecks/useConflictChecksEnabled';
import CourtRulesPanel from '../CourtRules/CourtRulesPanel';
import useCourtRulesEnabled from '../CourtRules/useCourtRulesEnabled';
import { AppTabView, AppTab, AppMenu, AppButton, AppBadge, AppTextInput } from '../shared';
import useUrlTabState from '../shared/useUrlTabState';
import './CaseDetails.css';
import './CaseDetailsModals.css';

// Cache configuration for instant case details loading using stale-while-revalidate pattern.
// This enables the top section (matter number, clients, case info, opposing counsel) to appear instantly
// by serving cached data immediately while fetching fresh data in the background.
import AppIcon from "../shared/AppIcon";const CACHE_KEY_PREFIX = 'case_details_cache_';
const CACHE_STALE_MS = 5 * 60 * 1000; // 5 minutes - data is considered stale after this
const CASE_DETAILS_TAB_IDS = ['clockify', 'xero', 'activity', 'documents', 'attributes', 'trello-tasks', 'payment-instructions'];
// Appended last (only when the backend flag is on) so existing tab indices never shift.
const CASE_DETAILS_TAB_IDS_WITH_CONFLICTS = [...CASE_DETAILS_TAB_IDS, 'conflicts'];
const CASE_DETAILS_TAB_IDS_WITH_COURT_RULES = [...CASE_DETAILS_TAB_IDS, 'court-rules'];
const CASE_DETAILS_TAB_IDS_WITH_BOTH = [...CASE_DETAILS_TAB_IDS, 'conflicts', 'court-rules'];



function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}



function CaseDetails() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  let { MatterNumber } = useParams();
  const [matterNumber, setMatterNumber] = useState(MatterNumber);
  const matter = useSelector((state) => state.caseDetails.matter);
  const [clients, setClients] = useState([]);
  const [opposingCounsel, setOpposingCounsel] = useState(null);
  const [, setAttorneys] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [journals, setJournals] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);
  const conflictChecksEnabled = useConflictChecksEnabled();
  const courtRulesEnabled = useCourtRulesEnabled();
  const caseDetailsTabIds = useMemo(() => {
    if (conflictChecksEnabled && courtRulesEnabled) return CASE_DETAILS_TAB_IDS_WITH_BOTH;
    if (conflictChecksEnabled) return CASE_DETAILS_TAB_IDS_WITH_CONFLICTS;
    if (courtRulesEnabled) return CASE_DETAILS_TAB_IDS_WITH_COURT_RULES;
    return CASE_DETAILS_TAB_IDS;
  }, [conflictChecksEnabled, courtRulesEnabled]);
  const {
    activeIndex: tabIndex,
    onTabChange: handleTabChange,
    setActiveIndex: setTabIndex
  } = useUrlTabState(caseDetailsTabIds);
  const [trelloTaskFormVisible, setTrelloTaskFormVisible] = useState(false);
  const [demandSentAttributeFormVisible, setDemandSentAttributeFormVisible] = useState(false);
  const [clientUpdateWorkVisible, setClientUpdateWorkVisible] = useState(false);
  const prevSubstage = usePrevious(matter?.Substage);
  const prevStage = usePrevious(matter?.Stage);
  const prevMatterNumber = usePrevious(MatterNumber);
  const showToast = useContext(ToastContext);
  const matterMenu = useRef(null);
  const [matterMenuItems, setMatterMenuItems] = useState([]);
  const [error, setError] = useState(false);

  // Get matter data passed from the matters table for instant rendering
  const preloadedMatterData = location.state?.matterData;

  useEffect(() => {
    const handleBeforeUnload =

    (event) => {
      // Your code here, for example:
      console.log('Leaving the page');
      // Optionally, you can show a confirmation dialog
      //   event.preventDefault();
      //   event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup the event listener when the component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      dispatch(setMatter(null));
    };
  }, []);



  const loadFromCache = useCallback((matterNum) => {
    try {
      const cached = localStorage.getItem(CACHE_KEY_PREFIX + matterNum);
      if (!cached) return null;
      const { timestamp, data } = JSON.parse(cached);
      // Allow stale data for instant display - we'll revalidate in background
      if (Date.now() - timestamp < CACHE_STALE_MS * 2) {
        return data;
      }
      return null;
    } catch {
      return null;
    }
  }, []);



  const saveToCache = useCallback((matterNum, data) => {
    try {
      localStorage.setItem(CACHE_KEY_PREFIX + matterNum, JSON.stringify({
        timestamp: Date.now(),
        data
      }));
    } catch (e) {
      console.warn('Failed to cache case details:', e);
    }
  }, []);



  const fetchMatter = async () => {
    try {
      // INSTANT: If we have preloaded data from the matters table, use it immediately
      // This makes the top section appear in 0ms when navigating from the matters list
      // The matters table includes Client, Attorney, and Defendant as comma-separated strings
      if (preloadedMatterData) {
        console.log('Pre-populating with data from matters table');

        // Parse the comma-separated strings into arrays of objects for display
        // The matters table has these as strings like "John Doe, Jane Smith"
        const parseToArray =

        (str, key) => {
          if (!str) return [];
          return str.split(',').map((name) => ({ [key]: name.trim() }));
        };

        const preloadedMatter = {
          MatterNumber: preloadedMatterData.MatterNumber,
          Jurisdiction: preloadedMatterData.Jurisdiction,
          Stage: preloadedMatterData.Stage,
          Substage: preloadedMatterData.Substage,
          CaseNumber: preloadedMatterData.CaseNumber,
          Hidden: preloadedMatterData.Hidden,
          // Parse the comma-separated strings from the matters table
          // These are used for display only in CaseInfo - the full data with IDs
          // will be loaded from the API before editing is possible
          attorneys: parseToArray(preloadedMatterData.Attorney, 'Name'),
          defendants: parseToArray(preloadedMatterData.Defendant, 'PartyName'),
          clients: parseToArray(preloadedMatterData.Client, 'ClientName')
        };
        dispatch(setMatter(preloadedMatter));

        // NOTE: We intentionally do NOT set clients/attorneys state here
        // Those states need complete data with IDs for editing to work correctly
        // They will be populated when the API call completes (~50-100ms)
        // The CaseInfo component displays from matter.attorneys/defendants/clients
        // while the Clients component uses the clients state which needs full data

        console.log("Matter (preloaded with clients/attorneys/defendants for display):", preloadedMatter);
      }

      // Try to load from cache for instant display (stale-while-revalidate pattern)
      const cachedData = loadFromCache(MatterNumber);
      if (cachedData && !preloadedMatterData) {
        console.log('Loaded case details from cache - showing stale data while revalidating');
        // Set all state from cached data immediately
        let m = cachedData.matter;
        m.attorneys = cachedData.attorneys;
        m.defendants = cachedData.defendants;
        m.clients = cachedData.clients;
        m.opposingCounsel = cachedData.opposingCounsel;
        dispatch(setMatter(m));
        setClients(cachedData.clients);
        setOpposingCounsel(cachedData.opposingCounsel || null);
        setAttorneys(cachedData.attorneys);
        setInvoices(cachedData.invoices);
        setJournals(cachedData.journals);
      }

      // PRIORITY 1: Fetch essential data first (matter, clients, attorneys, defendants)
      // This is a lightweight query that returns quickly, filling in details not in the matters table
      const essentialResponse = await api.get(`/case_details_essential/${MatterNumber}`);
      const essentialData = essentialResponse.data;

      // Update state with essential data - this adds clients, attorneys, defendants
      let m = essentialData.matter;
      m.attorneys = essentialData.attorneys;
      m.defendants = essentialData.defendants;
      m.clients = essentialData.clients;
      m.opposingCounsel = essentialData.opposingCounsel;
      dispatch(setMatter(m));
      console.log("Matter (essential):", essentialData.matter);

      setClients(essentialData.clients);
      console.log("Clients:", essentialData.clients);

      setOpposingCounsel(essentialData.opposingCounsel || null);
      console.log("Opposing counsel:", essentialData.opposingCounsel);

      setAttorneys(essentialData.attorneys);
      console.log("Attorneys:", essentialData.attorneys);

      // PRIORITY 2: Fetch heavier tab data in parallel (runs in background after top section is visible)
      // This doesn't block the top section from rendering
      const xeroResponse = await api.get(`/case_details_xero/${MatterNumber}`);
      const xeroData = xeroResponse.data;

      setInvoices(xeroData.invoices);
      console.log("Invoices:", xeroData.invoices);

      setJournals(xeroData.journals);
      console.log("Journals:", xeroData.journals);

      // Save to cache for next visit
      saveToCache(MatterNumber, {
        matter: essentialData.matter,
        attorneys: essentialData.attorneys,
        defendants: essentialData.defendants,
        clients: essentialData.clients,
        opposingCounsel: essentialData.opposingCounsel,
        invoices: xeroData.invoices,
        journals: xeroData.journals
      });
    } catch (error) {
      // Check if the error is due to an expired token and is being handled
      if (error.response && error.response.status === 401 && error.config._retry) {
        console.log('Waiting for token refresh...');
        // You might want to implement a delay or a mechanism to wait for the token refresh to complete
      } else {
        // Handle other errors
        setError(true);
      }
    }
  };


  useEffect(() => {
    fetchMatter();
    document.title = MatterNumber;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (clients.length === 0) {
      setClients([{
        ClientName: "",
        ClientPhone: "",
        ClientEmail: "",
        AdditionalPhone: "",
        AdditionalEmails: "",
        Notes: "",
        PrimaryClient: false,
        IsNew: true
      }]);
    }
    console.log("Clients:", clients);
  }, [clients]);

  useEffect(

  () => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }

    const handleClickOutside =

    (event) => {
      if (isEditing && inputRef.current && !inputRef.current.contains(event.target)) {
        setIsEditing(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing]);

  useEffect(

  () => {
    if (
    matter?.Substage !== prevSubstage &&
    prevSubstage && (
    !prevMatterNumber || MatterNumber === prevMatterNumber))
    {
      if (
      matter?.Substage === 'Agreement Executed & Payment(s) Pending' ||
      matter?.Substage === 'Settled in Principal')
      {
        setTabIndex(5);
        setTrelloTaskFormVisible(true);
      } else if (
      matter?.Substage === 'Payments Complete/Fully Resolved')
      {
        setTabIndex(1);
        showToast({ severity: 'info', summary: 'Resolved', detail: 'Financials update automatically from Xero.' });
      }
    }
  }, [matter?.Substage]);

  useEffect(

  () => {
    if (
    matter?.Stage !== prevStage &&
    prevStage && (
    !prevMatterNumber || MatterNumber === prevMatterNumber))
    {
      if (
      matter?.Stage === 'Withdrawn')
      {
        setTabIndex(5);
        showToast({ severity: 'info', summary: 'Withdrawn', detail: 'Be sure to delete any queued Trello Tasks' });
      } else if (
      matter?.Stage === 'Demand Sent')
      {
        setTabIndex(4);
        setDemandSentAttributeFormVisible(true);
      }
    }
  }, [matter?.Stage]);

  const handleChange =

  (e) => {
    setMatterNumber(e.target?.value);
  };

  const handleKeyDown =

  (e) => {
    if (e.key === 'Enter') {
      // Invoke your submit function here
      submitMatterNumber();
    }
  };

  const submitMatterNumber =

  async () => {
    try {
      const response = await api.post('/api/matter_number', {
        MatterNumber: MatterNumber,
        NewMatterNumber: matterNumber
      });
      if (response.status === 201) {
        console.log('Matter number updated successfully');
        setMatterNumber(matterNumber); // Updating the local state to reflect the new matter number
        setIsEditing(false);
      } else {
        console.error('Error:', response.data.message);
      }
    } catch (e) {
      console.error('Error:', e);
    }
  };


  const deleteMatter =

  () => {
    api.delete(`/matter/${MatterNumber}`)
    .then((response) => {
      if (response.status === 200) {
        showToast({ severity: 'success', summary: 'Success', detail: 'Matter Deleted' });
        fetchMatter();
      } else {
        showToast({ severity: 'error', summary: 'Error', detail: 'Matter Deletion Failed' });
      }
    })
    .catch((e) => {
      console.error('Error:', e);
    });
  };

  const undeleteMatter =

  () => {
    api.post(`/undelete-matter/${MatterNumber}`)
    .then((response) => {
      if (response.status === 200) {
        showToast({ severity: 'success', summary: 'Success', detail: 'Matter Undeleted' });
        fetchMatter();
      } else {
        showToast({ severity: 'error', summary: 'Error', detail: 'Matter Undeletion Failed' });
      }
    })
    .catch((e) => {
      console.error('Error:', e);
    });
  };

  useEffect(() => {
    if (matter?.Hidden) {
      setMatterMenuItems([
      {
        label: 'Undelete Matter',
        icon: 'pi pi-circle',
        severity: 'success',
        command: () => {
          if (window.confirm('Are you sure you want to undelete this matter?')) {
            undeleteMatter();
          }
        }
      }]
      );
    } else {
      setMatterMenuItems([
      {
        label: 'Delete Matter',
        icon: 'pi pi-times',
        severity: 'danger',
        command: () => {
          if (window.confirm('Are you sure you want to delete this matter?')) {
            deleteMatter();
          }
        }
      }]
      );
    }
  }, [matter]);

  if (error) return <NotFoundPage />;

  if (matter) return (
    <div className="case-details">
			<TrelloTaskForm substage={matter.Substage} visible={trelloTaskFormVisible} setVisible={setTrelloTaskFormVisible} currentlyEditing={null} defaultType="DueDate" />
			<DemandSentAttributeForm visible={demandSentAttributeFormVisible} setVisible={setDemandSentAttributeFormVisible} attributes={matter?.attributes} fetchAttributes={fetchMatter} />
			<ClientUpdateWorkModal matterNumber={MatterNumber} visible={clientUpdateWorkVisible} onClose={() => setClientUpdateWorkVisible(false)} showToast={showToast} />
			<div className="case-details__header">
				{isEditing ?
        <AppTextInput
          variant="borderless"
          ref={inputRef}
          className="case-details__title-input"
          value={matterNumber || ''}
          onChange={(e) => handleChange(e)}
          onKeyDown={handleKeyDown} /> :


        <>
						<div className="case-details__header-main">
							<button type="button" className="case-details__back-button" onClick={() => navigate('/matters')}>
								<AppIcon icon="pi pi-arrow-left" aria-hidden="true" />
								<span>Back</span>
							</button>
							<div className="case-details__title-wrap">
								<span className="case-details__title-eyebrow">Case Details</span>
								<h1 className="case-details__title">{MatterNumber}</h1>
							</div>
						</div>
						<div className="case-details__header-actions">
							{matter?.Hidden &&
            <div className="case-details__status">
									<AppBadge severity='danger' size="large" value='Deleted' />
								</div>}

              {!matter?.Hidden && <AppButton
              className="case-details__client-update-button"
              icon="pi pi-file-edit"
              label="Prepare client update"
              variant="secondary"
              onClick={() => setClientUpdateWorkVisible(true)} />}

							<AppButton
              className="case-details__assistant-button"
              icon="pi pi-comments"
              label="Ask Assistant"
              variant="secondary"
              onClick={() => navigate(`/document_chat?matter=${encodeURIComponent(MatterNumber)}`)} />

							<AppMenu model={matterMenuItems} ref={matterMenu} />
							<AppButton
              className="case-details__menu-button"
              icon="pi pi-ellipsis-v"
              variant="secondary"
              onClick={(event) => matterMenu.current.toggle(event)} />

						</div>
					</>}

			</div>
			<div className="case-details__content">
				<section className="case-details__summary" aria-label="Matter summary">
					<div className='grid case-details__summary-grid'>
						<Clients
              clients={clients}
              setClients={setClients}
              opposingCounsel={opposingCounsel}
              setOpposingCounsel={setOpposingCounsel}
              matterNumber={MatterNumber}
              fetchMatter={fetchMatter} />

						<CaseInfo fetchMatter={fetchMatter} />
					</div>
				</section>
				<div className="big-box case-details__workspace">
						<div className="case-details__workspace-shell">

							<AppTabView
                activeIndex={tabIndex}
                onTabChange={handleTabChange}
                className="case-details__workspace-tabs">

								<AppTab header="Clockify">
									<div className='relative overflow-y-auto' style={{ height: '50rem' }}>
										<div className='h-full overflow-y-scroll'>
											<Billing />
										</div>
									</div>
								</AppTab>
								<AppTab header="Xero">
									<div className='relative overflow-y-auto' style={{ height: '50rem' }}>
										<div className='h-full overflow-y-scroll'>
											<XeroFinancials matterNumber={MatterNumber} stage={matter?.Stage} substage={matter?.Substage} />
                    <Invoices
                        invoices={invoices}
                        journals={journals} />

										</div>
									</div>
								</AppTab>
								<AppTab header="Activity">
									<div className='relative overflow-y-auto' style={{ height: '50rem' }}>
										<div className='h-full overflow-y-scroll'>
											<Activity matterNumber={MatterNumber} matter={matter} />
										</div>
									</div>
								</AppTab>
								<AppTab header="Documents">
									<MatterDocumentsTab matterNumber={MatterNumber} />

								</AppTab>
								<AppTab header="Attributes">
									<div className='relative overflow-y-auto' style={{ height: '50rem' }}>
										<div className='h-full overflow-y-scroll'>
											<Attributes matterNumber={MatterNumber} />
										</div>
									</div>
								</AppTab>
								<AppTab header="Trello Tasks">
									<div className='relative overflow-y-auto' style={{ height: '50rem' }}>
										<div className='h-full overflow-y-scroll'>
											<QueuedTasks matterNumber={MatterNumber} />
										</div>
									</div>
								</AppTab>
								<AppTab header="Payment instructions">
                  <PaymentInstructionsHistory matterNumber={MatterNumber} />
                </AppTab>
								{conflictChecksEnabled &&
                <AppTab header="Conflicts">
                  <ConflictCheckPanel subjectType="matter" matterNumber={MatterNumber} active={tabIndex === caseDetailsTabIds.indexOf('conflicts')} />
                </AppTab>}
								{courtRulesEnabled &&
                <AppTab header="Court rules">
                  <CourtRulesPanel matterNumber={MatterNumber} active={tabIndex === caseDetailsTabIds.indexOf('court-rules')} />
                </AppTab>}
							</AppTabView>
						</div>
				</div>
			</div>
		</div>);


  return null;
}

export default CaseDetails;
